import { useState } from 'react';
import * as Yup from 'yup';

import { Button } from 'components/button/Button';
import { Formik } from 'components/form/formik';
import { InputField } from 'components/input/InputField';
import { InputWrapper } from 'components/InputWrapper';
import { ToggleGroup } from 'components/toggle-group/ToggleGroup';
import { useTranslation } from 'contexts/translation-context';
import { getDisplayError } from '@utils/get-display-error';

import type { ConnectorFieldSpec, ConnectorFormSpec, TelemetryOwnerKindName } from '../endpoints/shared.types';
import { saveTelemetryConnection, type TelemetryParty } from '../models';

interface IConnectionFormProps {
  spec: ConnectorFormSpec;
  party?: TelemetryParty;
  /** An internal user owns no party, so they have to name the connection's owner themselves. */
  requiresOwner?: boolean;
  onSaved: () => void;
  /** Leave the form without saving — back to the list of providers. */
  onCancel: () => void;
}

interface IFormValues {
  label: string;
  config: Record<string, string>;
  credentials: Record<string, string>;
  ownerKind: TelemetryOwnerKindName;
  supplierId: string;
}

function validationFor(field: ConnectorFieldSpec): Yup.AnySchema {
  if (field.type === 'number') {
    return field.required
      ? Yup.number().typeError('Voer een getal in').integer().positive().required('Verplicht')
      : Yup.number().typeError('Voer een getal in').integer().positive();
  }
  return field.required ? Yup.string().required('Verplicht') : Yup.string();
}

function initialValuesFor(spec: ConnectorFormSpec): IFormValues {
  return {
    label: spec.displayName,
    config: Object.fromEntries(spec.configFields.map((field) => [field.name, ''])),
    credentials: Object.fromEntries(spec.credentialFields.map((field) => [field.name, ''])),
    // Suivo's trackers are installed in our own trailers, so its account belongs to the company; a
    // FleetMaster key follows a carrier's trucks. It is only a default — the choice stays open.
    ownerKind: spec.provider === 'Suivo' ? 'Company' : 'Carrier',
    supplierId: '',
  };
}

function validationSchemaFor(spec: ConnectorFormSpec, requiresOwner: boolean): Yup.AnySchema {
  return Yup.object({
    label: Yup.string().trim().required('Verplicht').max(100),
    config: Yup.object(Object.fromEntries(spec.configFields.map((field) => [field.name, validationFor(field)]))),
    credentials: Yup.object(
      Object.fromEntries(spec.credentialFields.map((field) => [field.name, validationFor(field)])),
    ),
    ownerKind: requiresOwner ? Yup.string().oneOf(['Carrier', 'Company']).required('Verplicht') : Yup.string().strip(),
    // Only a carrier connection names one: the company is a single account on this platform and has no id
    // to type.
    supplierId: requiresOwner
      ? Yup.string().when('ownerKind', {
          is: 'Carrier',
          then: (schema) => schema.trim().required('Verplicht'),
          otherwise: (schema) => schema.strip(),
        })
      : Yup.string().strip(),
  });
}

/**
 * The "connect this provider" form, rendered from the connector's own field spec.
 *
 * A new connector therefore needs no new form code: it declares its fields and this form renders them,
 * posting non-secret values as `config` and secret ones as `credentials`.
 *
 * Mount one per chosen provider — it seeds its values from `spec` once, so a different spec has to be a
 * different instance (the dialog keys it by provider).
 */
export const ConnectionForm = ({ spec, party, requiresOwner = false, onSaved, onCancel }: IConnectionFormProps) => {
  const { i18n } = useTranslation();
  const [error, setError] = useState('');

  const handleSubmit = async (values: IFormValues) => {
    setError('');
    try {
      const config: Record<string, string | number> = {};
      for (const field of spec.configFields) {
        const raw = values.config[field.name] ?? '';
        config[field.name] = field.type === 'number' ? Number(raw) : raw;
      }

      const credentials: Record<string, string> = {};
      for (const field of spec.credentialFields) {
        credentials[field.name] = values.credentials[field.name] ?? '';
      }

      // The owner is sent by name — `Company` for our own account, a carrier id for a carrier's — and only
      // an internal user is asked for it at all.
      const owner: { ownerKind: TelemetryOwnerKindName; supplierId?: number } = { ownerKind: values.ownerKind };
      if (values.ownerKind === 'Carrier' && values.supplierId.trim()) {
        owner.supplierId = Number(values.supplierId);
      }

      await saveTelemetryConnection(
        {
          provider: spec.provider,
          label: values.label,
          config,
          credentials,
          ...(requiresOwner ? owner : {}),
        },
        party,
      );

      onSaved();
    } catch (err) {
      setError(getDisplayError(err));
    }
  };

  return (
    <Formik<IFormValues>
      initialValues={initialValuesFor(spec)}
      validationSchema={validationSchemaFor(spec, requiresOwner)}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit: submit, isSubmitting, values, setFieldValue }) => (
        <form
          onSubmit={(evt) => {
            evt.preventDefault();
            submit();
          }}
        >
          <div className="grid gap-4">
            <InputField
              name="label"
              labelText={i18n('liveTracking.connectionLabel')}
              helperText={i18n('liveTracking.connectionLabelHelp')}
            />

            {spec.configFields.map((field) => (
              <InputField
                key={field.name}
                name={`config.${field.name}`}
                labelText={field.label}
                type={field.type}
                helperText={field.helpKey ? i18n(field.helpKey) : undefined}
              />
            ))}

            {spec.credentialFields.map((field) => (
              <InputField
                key={field.name}
                name={`credentials.${field.name}`}
                labelText={field.label}
                type={field.type}
                helperText={field.helpKey ? i18n(field.helpKey) : undefined}
              />
            ))}

            {requiresOwner && (
              <>
                <InputWrapper labelText={i18n('liveTracking.ownerKindLabel')}>
                  <ToggleGroup<TelemetryOwnerKindName>
                    value={values.ownerKind}
                    onChange={(value) => setFieldValue('ownerKind', value)}
                    options={[
                      { value: 'Company', label: i18n('liveTracking.ownerKindCompany') },
                      { value: 'Carrier', label: i18n('liveTracking.ownerKindCarrier') },
                    ]}
                  />
                </InputWrapper>

                {values.ownerKind === 'Carrier' && (
                  <InputField
                    name="supplierId"
                    labelText={i18n('liveTracking.ownerLabel')}
                    type="number"
                    helperText={i18n('liveTracking.ownerHelp')}
                  />
                )}
              </>
            )}

            <p className="text-sm text-fg-muted">{i18n('liveTracking.credentialsNote')}</p>
          </div>

          {error && <div className="mt-4 text-sm text-negative-text">{error}</div>}

          <div className="mt-6 flex justify-between">
            <Button onTrigger={onCancel} isDisabled={isSubmitting}>
              {i18n('liveTracking.back')}
            </Button>
            <Button type="submit" color="primary" isLoading={isSubmitting} isDisabled={isSubmitting}>
              {i18n('liveTracking.connect')}
            </Button>
          </div>
        </form>
      )}
    </Formik>
  );
};
